import type { AddressedRecord } from './article-address';
import { type ArticleCritiqueReport, locateQuoteInArticle } from './article-critique';
import { type CritiqueFinding, articleCritiqueFindingKey } from './article-critique-confirmation';

/**
 * A NOTE AN ADDRESS PASS COULD NOT FIX STAYS OPEN.
 *
 * Pilot job 14efdc24 on topic fc38c408: four notes, three edited, one refused (n2). The new version's header read
 * "Open notes 4 -> 0". It was wrong: the recheck's FRESH critique happened to return no findings, while n2's
 * passage was word for word what it had been. A critique is a model's opinion and an opinion on a passage nobody
 * changed is not new information (`article-critique-reuse.ts`), so "the model did not raise it again" cannot close a
 * note.
 *
 * The rule, in code:
 *
 *   a critique note the pass refused stays OPEN on the version the pass made while its quoted passage is still in
 *   the text, word for word. It closes only when that text changes, or when the operator accepts it.
 *
 * `carriedOpenFindings` reads the refusals the pass recorded on the version it made (`addressed.rejected`, which
 * keeps each refused critique finding whole) and returns the ones still open. The recheck of that version merges them
 * into its critique (`withCarriedOpenFindings`), so every surface that counts findings (the dock, the release gate,
 * the Map and Table scores, the next address pass) counts them without knowing about this module. The critique is
 * told about them too, so it does not contradict them. A wrong-law CLAIM needs no carrying: the claim check carries
 * a verdict on an unchanged sentence over (`article-factcheck-carry.ts`).
 */

export interface CarriedFinding {
  finding: CritiqueFinding;
  key: string;
  /** Why the pass could not fix it, in words for the operator. */
  why: string;
}

const flat = (text: string): string =>
  text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[`*_~]+/g, '')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

/** Whether a critique already carries a finding that is this note: the same key, or the same passage under the same dimension. */
function represented(findings: readonly CritiqueFinding[], note: CritiqueFinding): boolean {
  const key = articleCritiqueFindingKey(note);
  const quote = flat(note.quote);
  return findings.some(
    (finding) =>
      articleCritiqueFindingKey(finding) === key ||
      (finding.dimension === note.dimension && flat(finding.quote) === quote),
  );
}

export function carriedOpenFindings(input: {
  /** The record on the version the pass MADE (`readAddressedRecord`). Null for a version no pass made. */
  addressed: Pick<AddressedRecord, 'rejected'> | null | undefined;
  /** The text of the version, as it stands now. */
  markdown: string;
  /** The operator's decisions on this version's findings. */
  reviewedKeys: ReadonlySet<string>;
  /** The findings a critique of this version already has; a note it already raised is not carried twice. */
  fresh?: readonly CritiqueFinding[];
}): CarriedFinding[] {
  const out: CarriedFinding[] = [];
  for (const entry of input.addressed?.rejected ?? []) {
    if (entry.kind !== 'critique' || !entry.finding) continue;
    // A note whose passage was not in the text the pass saw could not have been fixed, and is not carried.
    if (entry.code === 'locate') continue;
    const key = articleCritiqueFindingKey(entry.finding);
    if (input.reviewedKeys.has(key)) continue;
    if (locateQuoteInArticle(input.markdown, entry.finding.quote) === null) continue;
    if (represented(input.fresh ?? [], entry.finding)) continue;
    if (out.some((carried) => carried.key === key)) continue;
    out.push({ finding: entry.finding, key, why: entry.why });
  }
  return out;
}

/** The critique with the carried notes added to its findings; a critique that could not run is returned as it is. */
export function withCarriedOpenFindings(
  critique: ArticleCritiqueReport,
  carried: readonly CarriedFinding[],
): ArticleCritiqueReport {
  if (!carried.length || critique.status === 'unavailable') return critique;
  const added = carried
    .filter((entry) => !represented(critique.findings, entry.finding))
    .map((entry) => entry.finding);
  return added.length ? { ...critique, findings: [...critique.findings, ...added] } : critique;
}

/**
 * How many of an address pass's refused notes are still open on the version, given the passages (quotes) of the
 * findings and claim fixes that are open now. The panel says "3 addressed, 1 couldn't be addressed, still open"
 * from this, not from the difference of two counts a fresh critique can move on its own.
 */
export function stillOpenAfterAddress(
  addressed: Pick<AddressedRecord, 'rejected'> | null | undefined,
  openQuotes: readonly string[],
): number {
  const open = new Set(openQuotes.map(flat));
  const seen = new Set<string>();
  for (const entry of addressed?.rejected ?? []) {
    if (entry.code === 'locate') continue;
    const quote = flat(entry.quote);
    if (open.has(quote)) seen.add(quote);
  }
  return seen.size;
}

/**
 * The line a version an address pass made opens with. It names what the pass did and what it left, in those words,
 * and never a bare "open notes 4 -> 0": that difference moves when a fresh critique does, and a note the pass
 * refused is still open whatever it says. `stillOpen` is null while the version's checks have not finished; a
 * refused note whose passage is unchanged is open, so it is read as open until the checks say otherwise.
 */
export function addressOutcomeLine(input: {
  addressed: number;
  refused: number;
  stillOpen: number | null;
}): string {
  const done = `${input.addressed} addressed`;
  if (input.refused === 0) return `${done}.`;
  const open = input.stillOpen ?? input.refused;
  const couldNot = `${input.refused} couldn't be addressed`;
  if (open >= input.refused)
    return `${done}, ${couldNot} and ${input.refused === 1 ? 'is' : 'are'} still open.`;
  if (open === 0) return `${done}, ${couldNot} and the text they quote has since changed.`;
  return `${done}, ${couldNot}, ${open} still open.`;
}
